import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, IconButton, Modal, TextField, Typography } from '@mui/material';
import { Close as CloseIcon, Email as EmailIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import InstagramIcon from './icons/InstagramIcon';
import { useNotification } from '../../providers/NotificationContext';
import { RootState } from '../../store';
import unitService, { ListingShareEmailRequest } from '../../services/unitService';
import AsyncButtonContent, { asyncButtonState, useSuccessFlash } from '../motion/AsyncButtonContent';

export type ShareEmailListingDetails = Omit<ListingShareEmailRequest, 'senderName' | 'senderMessage' | 'recipientEmail'>;
export type ShareAction = 'email' | 'whatsapp' | 'imessage' | 'instagram' | 'copy';
export type ShareEmailAction = {
  send: (recipientEmail: string) => Promise<{ message?: string }>;
};
const DEFAULT_SENDER_MESSAGE = 'Thought you might like this listing.';
const MAX_RECIPIENT_EMAILS = 5;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  shareUrl: string;
  title?: string;
  dialogTitle?: string;
  description?: string;
  emailListing?: ShareEmailListingDetails;
  emailAction?: ShareEmailAction;
  actions?: ShareAction[];
  zIndex?: number;
}
const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onClose,
  shareUrl,
  title = 'Check out this property',
  dialogTitle = 'Share the apartment',
  description = '',
  emailListing,
  emailAction,
  actions,
  zIndex
}) => {
  const {
    success,
    error: notifyError
  } = useNotification();
  const { user, profile } = useSelector((state: RootState) => state.auth);
  const defaultSenderName = useMemo(() => {
    const profileName = `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim();
    if (profileName) return profileName;
    if (profile?.nickname?.trim()) return profile.nickname.trim();
    const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    if (userName) return userName;
    const userNickname = String((user as any)?.nickname || (user as any)?.nickName || '').trim();
    if (userNickname) return userNickname;
    return '';
  }, [profile, user]);
  const [isEmailFormOpen, setIsEmailFormOpen] = useState(false);
  const [senderName, setSenderName] = useState('');
  const [senderMessage, setSenderMessage] = useState(DEFAULT_SENDER_MESSAGE);
  const [recipientInput, setRecipientInput] = useState('');
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
  const [emailError, setEmailError] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSent, flashEmailSent] = useSuccessFlash();
  const isActionEnabled = (action: ShareAction) => !actions || actions.includes(action);
  const canShareByEmail = Boolean(emailListing || emailAction);

  useEffect(() => {
    if (!open) {
      setIsEmailFormOpen(false);
      setSenderName(defaultSenderName);
      setSenderMessage(DEFAULT_SENDER_MESSAGE);
      setRecipientInput('');
      setRecipientEmails([]);
      setEmailError('');
      setIsSendingEmail(false);
      return;
    }

    setSenderName(current => current || defaultSenderName);
  }, [defaultSenderName, open]);

  const handleEmailShare = () => {
    if (emailListing) {
      setSenderName(current => current || defaultSenderName);
    }
    setIsEmailFormOpen(true);
    setEmailError('');
  };
  const parseEmailTokens = (value: string) =>
    value.split(/[\s,;]+/).map(item => item.trim()).filter(Boolean);
  const validateRecipientEmails = (emails: string[]) => {
    const uniqueEmails: string[] = [];
    for (const email of emails) {
      const normalizedEmail = email.trim();
      if (!normalizedEmail) continue;
      if (!EMAIL_PATTERN.test(normalizedEmail)) {
        return {
          emails: uniqueEmails,
          error: `Please enter a valid email address: ${normalizedEmail}`,
        };
      }
      if (!uniqueEmails.some(item => item.toLowerCase() === normalizedEmail.toLowerCase())) {
        uniqueEmails.push(normalizedEmail);
      }
    }
    if (uniqueEmails.length > MAX_RECIPIENT_EMAILS) {
      return {
        emails: uniqueEmails.slice(0, MAX_RECIPIENT_EMAILS),
        error: `Please enter no more than ${MAX_RECIPIENT_EMAILS} recipient emails.`,
      };
    }
    return {
      emails: uniqueEmails,
      error: '',
    };
  };
  const addRecipientEmails = (value: string) => {
    const tokens = parseEmailTokens(value);
    if (tokens.length === 0) return false;
    const nextRecipients = [...recipientEmails, ...tokens];
    const validation = validateRecipientEmails(nextRecipients);
    if (validation.error) {
      setEmailError(validation.error);
      return false;
    }
    setRecipientEmails(validation.emails);
    setRecipientInput('');
    setEmailError('');
    return true;
  };
  const handleRecipientKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',' || event.key === 'Tab') {
      if (!recipientInput.trim()) return;
      event.preventDefault();
      addRecipientEmails(recipientInput);
    }
  };
  const handleRecipientPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData('text');
    if (!pastedText || parseEmailTokens(pastedText).length < 2) return;
    event.preventDefault();
    addRecipientEmails(pastedText);
  };
  const handleRemoveRecipient = (email: string) => {
    setRecipientEmails(current => current.filter(item => item !== email));
    setEmailError('');
  };
  const handleSendEmailShare = async () => {
    const inputTokens = parseEmailTokens(recipientInput);
    const validation = validateRecipientEmails([...recipientEmails, ...inputTokens]);
    const emails = validation.emails;
    const trimmedSenderName = senderName.trim();
    const trimmedSenderMessage = senderMessage.trim();

    if (!canShareByEmail) {
      setEmailError('Email sharing is unavailable.');
      return;
    }
    if (emailListing && !trimmedSenderName) {
      setEmailError('Please enter your name.');
      return;
    }
    if (emailListing && !trimmedSenderMessage) {
      setEmailError('Please enter a message.');
      return;
    }
    if (emailListing && trimmedSenderMessage.length > 200) {
      setEmailError('Message must be 200 characters or fewer.');
      return;
    }
    if (validation.error) {
      setEmailError(validation.error);
      return;
    }
    if (emails.length === 0) {
      setEmailError('Please enter at least one recipient email.');
      return;
    }

    setIsSendingEmail(true);
    setEmailError('');
    try {
      const response = emailListing
        ? await unitService.shareListingByEmail({
          ...emailListing,
          senderName: trimmedSenderName,
          senderMessage: trimmedSenderMessage,
          recipientEmail: emails.join(','),
        })
        : await emailAction!.send(emails.join(','));
      success(response.message || 'Email sent');
      // Hold the button's check for a moment before the modal closes.
      await flashEmailSent(600);
      onClose();
    } catch (error: any) {
      const message = error?.message || 'Failed to send email';
      setEmailError(message);
      notifyError(message);
    } finally {
      setIsSendingEmail(false);
    }
  };
  const handleWhatsAppShare = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareUrl)}`;
    window.open(whatsappUrl, '_blank');
  };
  const handleIMessageShare = () => {
    const text = `${title}${description ? ' - ' + description : ''}\n${shareUrl}`;
    // The `sms:` scheme opens Messages with prefilled text; iMessage crawls the
    // shareUrl and renders the OG card (same backend endpoint as WhatsApp).
    window.location.href = `sms:&body=${encodeURIComponent(text)}`;
  };
  const handleInstagramShare = async () => {
    try {
      await navigator.clipboard?.writeText(shareUrl);
      success('Link copied. Paste it into Instagram.');
    } catch (error) {
      console.error('Failed to copy Instagram share link:', error);
    }
    window.open('https://www.instagram.com/direct/inbox/', '_blank', 'noopener,noreferrer');
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      success('Link copied to clipboard');
      onClose();
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };
  if (!open) return null;
  return <Modal open={open} onClose={onClose} sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex,
  }}>
      <Box sx={{
      position: 'relative',
      width: {
        xs: '90%',
        sm: '500px'
      },
      bgcolor: 'rgba(241, 237, 235, 1)',
      borderRadius: '20px',
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
      p: 4,
      outline: 'none'
    }}>
        {/* Close Button */}
        <IconButton onClick={onClose} sx={{
        position: 'absolute',
        top: 16,
        right: 16,
        color: '#363434',
        '&:hover': {
          color: '#1A9CFF',
          bgcolor: 'transparent'
        }
      }}>
          <CloseIcon />
        </IconButton>

        {/* Title */}
        <Typography sx={{
        fontFamily: "'Montserrat', sans-serif",
        fontSize: "var(--type-h2)",
        fontWeight: 600,
        color: '#363434',
        mb: 4,
        pr: 4
      }}>
          {isEmailFormOpen ? 'Share by email' : dialogTitle}
        </Typography>

        {isEmailFormOpen ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            {emailListing && (
              <TextField
                label="Your name"
                value={senderName}
                onChange={event => setSenderName(event.target.value)}
                fullWidth
                size="small"
              />
            )}
            <Box>
              <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: 13, color: '#6B6B6B', mb: 0.75 }}>
                Recipients
              </Typography>
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                minHeight: 42,
                px: 1.25,
                py: 0.75,
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                bgcolor: '#FFFFFF',
                '&:focus-within': {
                  borderColor: '#1976d2',
                  borderWidth: '2px',
                  px: '9px',
                  py: '5px',
                },
              }}>
                {recipientEmails.map(email => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => handleRemoveRecipient(email)}
                    deleteIcon={<CloseIcon />}
                    size="small"
                    sx={{
                      maxWidth: '100%',
                      bgcolor: '#FFE8DD',
                      color: '#363434',
                      fontFamily: "'Montserrat', sans-serif",
                      '& .MuiChip-deleteIcon': {
                        color: '#9A8580',
                        fontSize: 18,
                        '&:hover': {
                          color: '#6B5B57',
                        },
                      },
                    }}
                  />
                ))}
                <Box
                  component="input"
                  value={recipientInput}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setRecipientInput(event.target.value)}
                  onKeyDown={handleRecipientKeyDown}
                  onPaste={handleRecipientPaste}
                  onBlur={() => addRecipientEmails(recipientInput)}
                  disabled={recipientEmails.length >= MAX_RECIPIENT_EMAILS}
                  placeholder={recipientEmails.length === 0 ? 'friend@example.com' : ''}
                  sx={{
                    flex: '1 1 180px',
                    minWidth: 120,
                    border: 0,
                    outline: 0,
                    p: '4px 0',
                    font: 'inherit',
                    fontFamily: "'Montserrat', sans-serif",
                    bgcolor: 'transparent',
                    '&::placeholder': {
                      color: '#9A9A9A',
                    },
                    '&:disabled': {
                      cursor: 'not-allowed',
                    },
                  }}
                />
              </Box>
              <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: 12, color: '#6B6B6B', mt: 0.5 }}>
                {recipientEmails.length} of {MAX_RECIPIENT_EMAILS} recipients
              </Typography>
            </Box>
            {emailListing && (
              <TextField
                label="Message"
                value={senderMessage}
                onChange={event => setSenderMessage(event.target.value.slice(0, 200))}
                fullWidth
                multiline
                minRows={3}
                inputProps={{ maxLength: 200 }}
                helperText={`${senderMessage.length}/200`}
              />
            )}
            {emailError && (
              <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: 13, color: '#D14343' }}>
                {emailError}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: 1 }}>
              <Button
                variant="text"
                onClick={() => setIsEmailFormOpen(false)}
                disabled={isSendingEmail}
                sx={{ color: '#5E5E5E', textTransform: 'none', fontFamily: "'Montserrat', sans-serif" }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSendEmailShare}
                disabled={isSendingEmail}
                sx={{
                  minWidth: 120,
                  borderRadius: '10px',
                  bgcolor: '#4C403E',
                  textTransform: 'none',
                  fontFamily: "'Montserrat', sans-serif",
                  '&:hover': { bgcolor: '#36302F' },
                }}
              >
                {/* Spinner/check stay white like the old spinner (the disabled button's text color is grey). */}
                <Box component="span" sx={{ display: 'inline-flex', color: isSendingEmail || emailSent ? '#FFFFFF' : undefined }}>
                  <AsyncButtonContent state={asyncButtonState(isSendingEmail, emailSent)} iconSize={18} loadingLabel="Sending email">
                    Send email
                  </AsyncButtonContent>
                </Box>
              </Button>
            </Box>
          </Box>
        ) : (
        <>
        {/* Social Media Icons */}
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-around',
        mb: 4,
        gap: 2
      }}>
          {canShareByEmail && isActionEnabled('email') && (
          <Box onClick={handleEmailShare} sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          '&:hover .icon-circle': {
            bgcolor: '#E0F2FF'
          }
        }}>
            <Box className="icon-circle" sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: '#D1EBFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s'
          }}>
              <EmailIcon sx={{
              fontSize: 32,
              color: '#1A9CFF'
            }} />
            </Box>
            <Typography sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "var(--type-small)",
            color: '#6B6B6B'
          }}>
              Email
            </Typography>
          </Box>
          )}

          {isActionEnabled('whatsapp') && (
          <>
          {/* WhatsApp */}
          <Box onClick={handleWhatsAppShare} sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          '&:hover .icon-circle': {
            bgcolor: '#D8F5E3'
          }
        }}>
            <Box className="icon-circle" sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: '#C8EFDC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s'
          }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" fill="#25D366" />
              </svg>
            </Box>
            <Typography sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "var(--type-small)",
            color: '#6B6B6B'
          }}>
              Whatsapp
            </Typography>
          </Box>
          </>
          )}

          {isActionEnabled('imessage') && (
          <>
          {/* iMessage */}
          <Box onClick={handleIMessageShare} sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          '&:hover .icon-circle': {
            bgcolor: '#CFE6FF'
          }
        }}>
            <Box className="icon-circle" sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: '#DCEBFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s'
          }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.477 2 2 5.94 2 10.8c0 2.77 1.46 5.24 3.74 6.86-.16 1.32-.7 2.5-1.5 3.46-.16.2-.05.5.2.54 1.78.27 3.5-.3 4.77-1.18.88.21 1.82.32 2.79.32 5.523 0 10-3.94 10-8.8S17.523 2 12 2Z" fill="#34DA50" />
              </svg>
            </Box>
            <Typography sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "var(--type-small)",
            color: '#6B6B6B'
          }}>
              iMessage
            </Typography>
          </Box>
          </>
          )}

          {isActionEnabled('instagram') && (
          <>
          {/* Instagram */}
          <Box onClick={handleInstagramShare} sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          '&:hover .icon-circle': {
            bgcolor: '#F5D8F0'
          }
        }}>
            <Box className="icon-circle" sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: '#F0D1EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s'
          }}>
              <InstagramIcon width={32} height={32} style={{
              color: '#C13584'
            }} />
            </Box>
            <Typography sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "var(--type-small)",
            color: '#6B6B6B'
          }}>
              Instagram
            </Typography>
          </Box>
          </>
          )}
        </Box>

        {isActionEnabled('copy') && (
        <>
        {/* Copy Link Button */}
        <Box onClick={handleCopyLink} sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        py: 2,
        borderRadius: '12px',
        border: '1px solid #E5E0DD',
        bgcolor: '#FAFAFA',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: '#F0F0F0',
          borderColor: '#1A9CFF'
        }
      }}>
          <CopyIcon sx={{
          fontSize: 20,
          color: '#363434'
        }} />
          <Typography sx={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: "var(--type-body)",
          fontWeight: 600,
          color: '#363434'
        }}>
            Copy link
          </Typography>
        </Box>
        </>
        )}
        </>
        )}
      </Box>
    </Modal>;
};
export default ShareModal;
